/**
 * Created by slava on 4/4/17.
 */

const BundleLoader = function () {
    this.handles = {};
    this.resourceHandles = {};
    this.unusedResources = {};
    this.unusedBundles = {};
};

cc.BundleLoader = BundleLoader;

BundleLoader.prototype.handleLoadFailure = function (name, errors) {
    const handle = this.getHandle(name);
    const onFailures = handle && handle.onFailures;

    this.deleteBundle(name);

    if (onFailures) {
        onFailures.forEach((onFailure) => {
            onFailure(errors);
        });
    }
};

BundleLoader.prototype.loadUrls = function (urls, options) {
    const start = Date.now();

    const canRetry = options.canRetry || (() => true);
    const onSuccess = options.onSuccess || (() => {});
    const onFailure = options.onFailure || (() => {});
    const trigger = options.onProgress ? (result, count, loadedCount) => {
        options.onProgress(loadedCount, count);
    } : undefined;
    let tries = options.tries || 3;
    let decode = true;

    let attempt = 0;

    const tryLoad = () => {
        if (!canRetry()) {
            return;
        }

        this.loadFiles(urls, {
            decode,
            imageCache: options.imageCache,
            trigger
        }, (errors) => {
            if (!canRetry()) {
                return;
            }

            if (!errors) {
                onSuccess();
                return;
            }

            attempt++;
            console.log("load failed", errors);

            if (errors.some((error) => error.status === 404)) {
                cleverapps.newVersionChecker.check();
            }

            if (attempt >= tries) {
                // sounds are optional - playSound skips a sound that is not loaded, the next session tries it again
                if (errors.every((error) => !error || error.type === "sound")) {
                    this.reportSoundsSkipped(errors, Date.now() - start);
                    onSuccess();
                    return;
                }

                this.reportLoadFailure(urls, errors, attempt, Date.now() - start);
                onFailure(errors, attempt);
                return;
            }

            // samsung cdn answers 404 for existing files for several seconds - retry longer
            if (tries < 10 && connector.platform.oneOf(connector.SAMSUNG) && errors.some((error) => error.status === 404)) {
                tries = 10;
            }

            // load images with <img> instead of createImageBitmap on the last third of tries as another kind of request - after a decode error, or for samsung
            if (attempt >= tries * 2 / 3 && errors.some((error) => error.type === "image"
                && (error.status === cc.LOAD_DECODE_ERROR_STATUS || connector.platform.oneOf(connector.SAMSUNG)))) {
                decode = false;
            }

            // cached content might be broken - reset cache before retrying
            errors.filter((error) => error.status === cc.LOAD_PARSE_ERROR_STATUS || error.status === cc.LOAD_DECODE_ERROR_STATUS
                || (error.status === 404 || error.status === 206) && connector.platform.oneOf(connector.SAMSUNG)).forEach((error) => {
                cc.loader.addNoCache(error.url);
            });

            if (attempt === 1) {
                this.reportLoadRetry(errors, Date.now() - start);
            }

            if (options.onRetry) {
                options.onRetry(attempt);
            }

            setTimeout(tryLoad, Math.min(10000, 1000 * Math.pow(2, attempt - 1)));
        });
    };

    tryLoad();
};

BundleLoader.prototype.loadFiles = function (urls, options, callback) {
    const simulatedError = cleverapps.config.debugMode && cleverapps.PageSession.get("simulatedLoadError");
    if (simulatedError) {
        setTimeout(() => callback(urls.map((url) => ({ url, status: simulatedError.status, errorMessage: "simulated load error" }))));
        return;
    }

    cc.loader.load(urls, options, callback);
};

BundleLoader.prototype.describeLoadErrors = function (errors) {
    const failed = errors.filter(Boolean);

    return {
        scene: cleverapps.environment.getScene(),
        errors: failed.map((error) => String(error.status)),
        urls: failed.map((error) => error.url),
        // one copy of each message - a network failure gives the same one for every url
        messages: cleverapps.unique(failed.map((error) => String(error.errorMessage)))
    };
};

BundleLoader.prototype.reportLoadRetry = function (errors, duration) {
    cleverapps.metrics.logEvent(cleverapps.EVENTS.DEBUG.LOAD_RETRY, {
        value: Math.round(duration / 1000),
        details: this.describeLoadErrors(errors)
    });
};

BundleLoader.prototype.reportSoundsSkipped = function (errors, duration) {
    cleverapps.metrics.logEvent(cleverapps.EVENTS.DEBUG.LOAD_SOUNDS_SKIPPED, {
        value: Math.round(duration / 1000),
        details: this.describeLoadErrors(errors)
    });
};

BundleLoader.prototype.reportLoadFailure = function (urls, errors, attempt, duration) {
    const seconds = Math.round(duration / 1000);

    cleverapps.metrics.logEvent(cleverapps.EVENTS.DEBUG.LOAD_FAILURE, {
        value: seconds,
        details: this.describeLoadErrors(errors)
    });

    const failed = Object.keys(errors);
    const details = failed.map((index) => `${errors[index].url} ${errors[index].status} ${errors[index].errorMessage}`).join(" | ");

    const navigation = window.performance && performance.getEntriesByType && performance.getEntriesByType("navigation")[0];
    const protocol = (navigation && navigation.nextHopProtocol) || "unknown";

    cleverapps.throwAsync({
        group: "loader_giveup",
        message: `gave up after ${attempt} tries in ${seconds}s over ${protocol} - ${failed.length} of ${urls.length} files failed - ${details}`
    });
};

BundleLoader.prototype.loadBundles = function (names, options) {
    options = options || {};

    const onSuccess = options.onSuccess || function () {};
    let onFailure = options.onFailure;

    const waiter = cleverapps.wait(names.length || 0, onSuccess);

    if (onFailure) {
        onFailure = cleverapps.once(onFailure);
    }

    for (let i = 0; i < names.length; i++) {
        const singleOptions = {
            onSuccess: waiter,
            onFailure,
            lazyUrls: options.lazyUrls
        };
        this.loadBundle(names[i], singleOptions);
    }
};

BundleLoader.prototype.getHandle = function (bundleName) {
    return this.handles[bundleName];
};

BundleLoader.prototype.isSingleLoaded = function (bundleName) {
    if (bundles[bundleName] && bundles[bundleName].injectTo) {
        return this.isVirtualLoaded(bundleName);
    }

    const handle = this.getHandle(bundleName);
    return handle && handle.loaded;
};

BundleLoader.prototype.isLoaded = function (bundleNames) {
    if (!Array.isArray(bundleNames)) {
        bundleNames = [bundleNames];
    }

    return bundleNames.every((name) => this.isSingleLoaded(name));
};

BundleLoader.prototype.isVirtualLoaded = function (bundleName) {
    const bundle = bundles[bundleName];
    for (const i in bundle.injectTo) {
        if (cleverapps.bundleLoader.isLoaded(bundle.injectTo[i])) {
            return true;
        }
    }

    return false;
};

BundleLoader.prototype.loadBundle = function (name, options) {
    const bundle = bundles[name];
    const handle = this.getHandle(name);

    options = options || {};
    const onSuccess = options.onSuccess;
    const onFailure = options.onFailure;
    const lazyUrls = [];

    if (!cleverapps.resolution.isInitialized()) {
        cleverapps.throwAsync("Can't load urls while resolution is not defined");
    }

    const dictionary = bundle.jsons[`dictionary_${cleverapps.settings.language}`]
        || bundle.urls[`dictionary_${cleverapps.settings.language}`];

    if (dictionary instanceof VirtualUrl) {
        lazyUrls.push(dictionary);
    }

    if (options.lazyUrls) {
        options.lazyUrls.forEach((url) => {
            if (url.getBundleName() === name) {
                lazyUrls.push(url);
            }
        });
    }

    const effects = bundle.jsons.effectsmap_json;

    if (bundle.virtual || bundle.stored) {
        onSuccess && onSuccess(bundle);
        return;
    }

    if (handle) {
        handle.counter++;
        if (handle.loaded) {
            // already loaded
            if (onSuccess) {
                onSuccess(bundle);
            }
        } else {
            // same bundle loading now
            if (onSuccess) {
                handle.onSuccesses.push(onSuccess);
            }
            if (onFailure) {
                handle.onFailures.push(onFailure);
            }
        }
        return;
    }

    let onSuccesses = [];
    if (onSuccess) {
        onSuccesses = Array.isArray(onSuccess) ? onSuccess : [onSuccess];
    }

    let onFailures = [];
    if (onFailure) {
        onFailures = Array.isArray(onFailure) ? onFailure : [onFailure];
    }

    const newHandle = {
        loaded: false,
        counter: 1,
        onSuccesses,
        onFailures
    };

    this.handles[name] = newHandle;

    if (dictionary) {
        Messages.addBundle(name);
    }

    if (effects) {
        cleverapps.effects.addBundle(name);
    }

    const onLoaded = cleverapps.wait(1 + lazyUrls.length, () => {
        if (this.getHandle(name) !== newHandle) {
            return;
        }

        newHandle.loaded = true;

        for (const pluginName in bundle.plugins) {
            this.installPlugin(pluginName);
        }

        bundle.onLoad(function () {
            for (let i = 0; i < newHandle.onSuccesses.length; i++) {
                onSuccesses[i].call(this, bundle);
            }

            delete newHandle.onSuccesses;
            delete newHandle.onFailures;
        });
    });

    const onFailed = cleverapps.once((errors) => {
        if (this.getHandle(name) !== newHandle) {
            return;
        }

        this.handleLoadFailure(name, errors);
    });

    const soundUrls = bundle.listSoundUrls();

    this.loadUrls(bundle.listUrls(), {
        canRetry: () => this.getHandle(name) === newHandle,
        onSuccess: () => {
            onLoaded();

            if (!cleverapps.getRequestParameters().testLoadingTime && soundUrls.length) {
                this.loadUrls(soundUrls, {
                    canRetry: () => this.getHandle(name) === newHandle,
                    onSuccess: () => {
                        cleverapps.audio.onSoundsLoaded(soundUrls);
                    }
                });
            }
        },
        onFailure: onFailed
    });

    lazyUrls.forEach((url) => {
        this.loadLazyResource(url, () => {
            onLoaded();
        }, () => {
            onFailed();
        });
    });
};

BundleLoader.prototype.preloadBundles = function (names) {
    const listUnloaded = cleverapps.toArray(names).filter((name) => name && bundles[name] && !this.isLoaded(name));

    cleverapps.bundleLoader.loadBundles(listUnloaded, {
        onSuccess: function () {
            cleverapps.bundleLoader.deleteBundles(listUnloaded);
        }
    });
};

BundleLoader.prototype.checkUrlUseInLoadedBundles = function (url) {
    for (const bundleName in this.handles) {
        const bundle = bundles[bundleName];

        if (bundle.listUrls().some((virtualUrl) => virtualUrl.getUrl() === url)) {
            return true;
        }

        if (bundle.listSoundUrls(true).some((virtualUrl) => virtualUrl.getUrl() === url)) {
            return true;
        }
    }

    for (const bundleName in this.resourceHandles) {
        if (this.resourceHandles[bundleName][url]) {
            return true;
        }
    }

    return false;
};

BundleLoader.prototype.deleteBundles = function (bundles) {
    if (bundles) {
        for (let i = 0; i < bundles.length; i++) {
            this.deleteBundle(bundles[i]);
        }
    }
};

BundleLoader.prototype.deleteBundle = function (bundleName) {
    const handle = this.handles[bundleName];
    if (handle === undefined) {
        return;
    }

    const PERMANENT_BUNDLES = ["ui", "main", "episodes_resources"];
    if (PERMANENT_BUNDLES.includes(bundleName)) {
        return;
    }

    handle.counter--;
    if (handle.counter > 0) {
        return;
    }

    Messages.removeBundle(bundleName);
    cleverapps.effects.removeBundle(bundleName);

    if (bundleName.indexOf("enemy") === 0) {
        if (!this.deleteStack) {
            this.deleteStack = {};
        }

        this.deleteStack[bundleName] = new Error().stack;
    }

    if (bundles[bundleName].stored) {
        return;
    }

    const bundle = bundles[bundleName];
    delete this.handles[bundleName];
    bundle.loaded = false;

    const urls = [
        ...bundle.listUrls(),
        ...bundle.listSoundUrls(true)
    ].map((url) => url.getUrl());

    for (const url in this.resourceHandles[bundleName]) {
        urls.push(url);
    }
    delete this.resourceHandles[bundleName];

    this.unusedBundles[bundleName] = true;

    urls.forEach((url) => this.markResourceUnused(url));
};

BundleLoader.prototype.loadLazyResource = function (resource, onSuccess, onFailure) {
    if (!(resource instanceof VirtualUrl)) {
        onFailure && onFailure();
        return;
    }

    const url = resource.getUrl();
    if (!url) {
        onFailure && onFailure();
        return;
    }

    const bundleName = resource.getBundleName();
    const bundle = bundles[bundleName];
    if (!bundle) {
        onFailure && onFailure();
        return;
    }

    const bundleHandle = this.handles[bundleName];
    if (!bundleHandle || !(bundleHandle.counter > 0)) {
        onFailure && onFailure();
        return;
    }

    if (!this.resourceHandles[bundleName]) {
        this.resourceHandles[bundleName] = {};
    }

    const resourceHandle = this.resourceHandles[bundleName][url];
    if (resourceHandle) {
        resourceHandle.counter++;
        if (resourceHandle.loaded) {
            if (onSuccess) {
                onSuccess();
            }
        } else {
            if (onSuccess) {
                resourceHandle.onSuccesses.push(onSuccess);
            }
            if (onFailure) {
                resourceHandle.onFailures.push(onFailure);
            }
        }
        return;
    }

    const onSuccesses = [];
    if (onSuccess) {
        onSuccesses.push(onSuccess);
    }

    const onFailures = [];
    if (onFailure) {
        onFailures.push(onFailure);
    }

    const newResourceHandle = {
        loaded: false,
        counter: 1,
        onSuccesses,
        onFailures
    };

    this.resourceHandles[bundleName][url] = newResourceHandle;

    this.loadUrls([url], {
        imageCache: true,
        canRetry: () => this.resourceHandles[bundleName] && this.resourceHandles[bundleName][url] === newResourceHandle,
        onSuccess: () => {
            if (newResourceHandle.loaded) {
                return;
            }

            newResourceHandle.loaded = true;

            bundle.tagTexture(url);

            const callbacks = newResourceHandle.onSuccesses;
            delete newResourceHandle.onSuccesses;
            delete newResourceHandle.onFailures;

            callbacks.forEach((callback) => callback());
        },
        onFailure: () => {
            const callbacks = newResourceHandle.onFailures;
            delete newResourceHandle.onSuccesses;
            delete newResourceHandle.onFailures;

            callbacks.forEach((callback) => callback());
        }
    });
};

BundleLoader.prototype.deleteLazyResource = function (resource) {
    if (!(resource instanceof VirtualUrl)) {
        return;
    }

    const url = resource.getUrl();
    if (!url) {
        return;
    }

    const bundleName = resource.getBundleName();
    if (this.resourceHandles[bundleName] === undefined) {
        return;
    }

    const resourceHandle = this.resourceHandles[bundleName][url];
    if (resourceHandle === undefined) {
        return;
    }

    resourceHandle.counter--;
    if (resourceHandle.counter > 0) {
        return;
    }

    delete this.resourceHandles[bundleName][url];

    this.markResourceUnused(url);
};

BundleLoader.prototype.isUnused = function (bundleName) {
    return !this.isLoaded(bundleName) && this.unusedBundles[bundleName];
};

BundleLoader.prototype.markResourceUnused = function (url) {
    // cancel a still-downloading resource right away; defer already-loaded ones to
    // clearUnusedResources so preloadBundles can keep them cached for a quick reload
    if (cc.loader.isLoading && cc.loader.isLoading(url)) {
        this.releaseResource(url);
    } else if (!this.checkUrlUseInLoadedBundles(url)) {
        this.unusedResources[url] = true;
    }
};

BundleLoader.prototype.releaseResource = function (url) {
    if (this.checkUrlUseInLoadedBundles(url)) {
        return;
    }

    const texture = cc.textureCache.getTextureForKey(url);
    if (texture) {
        cc.textureCache.removeTextureForKey(url);
    }

    cleverapps.audio.unload(url);

    // release() drops the loader cache entry and deletes the in-flight _queue entry,
    // so a still-downloading resource resolves to a no-op instead of caching after unload
    cc.loader.release(url);
};

BundleLoader.prototype.clearUnusedResources = function () {
    if (Object.keys(this.unusedBundles).length === 0 && Object.keys(this.unusedResources).length === 0) {
        return;
    }

    const resources = this.unusedResources;
    for (const url in resources) {
        this.releaseResource(url);
    }
    this.unusedResources = {};

    Object.keys(this.unusedBundles).forEach((bundleName) => {
        if (bundles[bundleName].injectTo || this.handles[bundleName]) {
            return;
        }

        const bundle = bundles[bundleName];

        for (const jsonId in bundle.jsons) {
            const virtualJson = bundle.jsons[jsonId];
            cleverapps.Spine.unloadCachedData(virtualJson);
        }

        for (const url in bundle.urls) {
            const virtualUrl = bundle.urls[url];
            cc.loader.releaseImageCache(virtualUrl.getUrl());
        }

        for (const pluginName in bundle.plugins) {
            if (!this.checkPluginUseInLoadedBundles(pluginName)) {
                this.uninstallPlugin(pluginName);
            }
        }

        bundle.onUnload();
    });

    this.unusedBundles = {};
};

BundleLoader.prototype.ensureSoundsLoaded = function (sounds) {
    if (cc.sys.isWebViewAudio) {
        return;
    }

    sounds = cleverapps.toArray(sounds).map((sound) => {
        sound = sound && sound.resolve();
        return sound instanceof SoundFrame ? sound.getTexture() : sound;
    }).filter((sound) => sound && !cc.loader.getRes(sound));

    cc.loader.load(sounds, () => {
        cleverapps.audio.onSoundsLoaded(sounds);
    });
};

BundleLoader.prototype.checkPluginUseInLoadedBundles = function (pluginName) {
    for (const bundleName in this.handles) {
        if (bundles[bundleName].plugins[pluginName]) {
            return true;
        }
    }

    return false;
};

BundleLoader.prototype.installPlugin = function (pluginName) {
    const plugin = BundleLoader.PLUGINS[pluginName];
    if (!plugin || plugin.installed) {
        return;
    }

    plugin.installed = true;

    const installers = plugin.installers;

    BundleLoader.installing = pluginName;
    try {
        for (let i = 0; i < installers.length; ++i) {
            installers[i]();
        }
    } finally {
        BundleLoader.installing = undefined;
    }
};

BundleLoader.prototype.uninstallPlugin = function (pluginName) {
    const plugin = BundleLoader.PLUGINS[pluginName];
    if (!plugin) {
        return;
    }

    plugin.unused = true;

    if (plugin.loaded && plugin.unused) {
        delete BundleLoader.PLUGINS[pluginName];
    }

    if (!plugin.installed) {
        return;
    }

    plugin.installed = false;

    const uninstallers = plugin.uninstallers;

    BundleLoader.uninstalling = pluginName;
    try {
        for (let i = uninstallers.length - 1; i >= 0; --i) {
            uninstallers[i]();
        }
    } finally {
        BundleLoader.uninstalling = undefined;
    }
};

BundleLoader.PLUGINS = {};

BundleLoader.definePlugin = function (pluginName, factory) {
    const plugin = BundleLoader.PLUGINS[pluginName];
    if (!plugin) {
        return;
    }

    BundleLoader.defining = pluginName;
    try {
        factory({
            register: function (options) {
                if (typeof options.install !== "function" || typeof options.uninstall !== "function") {
                    throw new Error(`plugin "${pluginName}" must define install and uninstall`);
                }

                plugin.installers.push(options.install);
                plugin.uninstallers.push(options.uninstall);
            }
        }, typeof _ === "undefined" ? undefined : _, cleverapps, bundles);
    } finally {
        BundleLoader.defining = undefined;
    }
};

BundleLoader.loadPlugin = function (pluginName, load, callback) {
    let plugin = BundleLoader.PLUGINS[pluginName];

    if (plugin && plugin.loaded) {
        callback();
        return;
    }

    if (plugin) {
        plugin.unused = false;
        plugin.callbacks.push(callback);
        return;
    }

    plugin = BundleLoader.PLUGINS[pluginName] = {
        loaded: false,
        unused: false,
        callbacks: [],

        installed: false,
        installers: [],
        uninstallers: []
    };
    plugin.callbacks.push(callback);

    load(cleverapps.waitNoMore(cc.LOAD_TIMEOUT, (error) => {
        const callbacks = plugin.callbacks;
        plugin.callbacks = [];

        if (error) {
            plugin.unused = true;
        } else {
            plugin.loaded = true;
        }

        if (plugin.unused && plugin === BundleLoader.PLUGINS[pluginName]) {
            delete BundleLoader.PLUGINS[pluginName];
        }

        callbacks.forEach((cb) => cb(error));
    }, { type: "plugin", status: cc.LOAD_TIMEOUT_STATUS, errorMessage: `Load plugin ${pluginName} timeout!` }));
};

cc.loader.register(["plugin"], {
    load: function (realUrl, url, res, cb) {
        const { name, srcs } = res.getUrl();

        BundleLoader.loadPlugin(name, (done) => this.loadScripts(srcs, done), cb);
    },

    loadScripts: function (srcs, cb) {
        const loadScript = (index) => {
            if (index === srcs.length) {
                cb();
                return;
            }

            const script = document.createElement("script");
            script.async = true;
            if (location.origin !== "file://") {
                script.crossOrigin = "anonymous";
            }
            script.src = srcs[index];

            script.onload = function () {
                script.remove();
                loadScript(index + 1);
            };
            script.onerror = function () {
                script.remove();
                cb({ type: "plugin", status: cc.LOAD_ERROR_STATUS, errorMessage: `Load ${srcs[index]} failed!` });
            };

            document.body.appendChild(script);
        };

        loadScript(0);
    }
});
